import { inspectPcmWav, friendlyPlaybackError } from './audioDiagnostics.js';
import { readBoundedArrayBuffer } from '../../cloudflare/requestBody.js';

// Independent lifecycle implementation from the permitted VoicePreview caller.
export function createVoicePreviewController({ audio, endpoint, token, onState = () => {},
  fetchImpl = globalThis.fetch, urlApi = URL, uuid = () => crypto.randomUUID() }) {
  let disposed = false, epoch = 0, controller = null, requestId = null, objectUrl = null, support = null, metadata = null;
  const publish = (phase, message) => { if (!disposed) onState({ phase, message, support, metadata }); };
  const headers = () => ({ authorization: `Bearer ${token}` });
  let mediaHandlers = null;
  function releaseAudio() {
    if (mediaHandlers) {
      audio.removeEventListener('ended', mediaHandlers.ended);
      audio.removeEventListener('error', mediaHandlers.failed);
      mediaHandlers = null;
    }
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    if (objectUrl) urlApi.revokeObjectURL(objectUrl);
    objectUrl = null; metadata = null;
  }
  function cancelPending() {
    epoch++; controller?.abort(); controller = null;
    const ownedRequest = requestId; requestId = null;
    if (ownedRequest) void fetchImpl(endpoint + '/cancel', {
      method: 'POST', headers: { ...headers(), 'content-type': 'application/json' },
      body: JSON.stringify({ requestId: ownedRequest }), cache: 'no-store', credentials: 'omit',
      redirect: 'error', signal: AbortSignal.timeout(10000),
    }).catch(() => {});
    releaseAudio();
  }
  function bindMedia(ownedUrl, mine) {
    const ownsSource = () => !disposed && mine === epoch && objectUrl === ownedUrl && audio.currentSrc === ownedUrl;
    const ended = () => { if (ownsSource() && audio.ended) publish('ready', 'Preview finished. Choose Play preview to hear it again.'); };
    const failed = () => { if (ownsSource() && audio.error) publish('error', 'Preview playback failed. Retry.'); };
    mediaHandlers = { ended, failed };
    audio.addEventListener('ended', ended); audio.addEventListener('error', failed);
  }
  async function json(response, signal) {
    const bytes = await readBoundedArrayBuffer(response, 65536, 'Preview status is too large.', signal);
    let value;
    try { value = JSON.parse(new TextDecoder().decode(bytes)); } catch { throw new Error('Preview status could not be read. Retry.'); }
    if (!response.ok) throw new Error(typeof value?.message === 'string' ? value.message : 'Preview request failed. Retry.');
    return value;
  }
  async function load() {
    if (disposed) return false;
    cancelPending(); const mine = epoch;
    support = { available: false, voices: [] };
    controller = new AbortController();
    const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]);
    publish('loading', 'Checking Voicebox presets…');
    try {
      const response = await fetchImpl(endpoint, { headers: headers(), signal, cache: 'no-store', credentials: 'omit', redirect: 'error' });
      const value = await json(response, signal);
      if (disposed || mine !== epoch) return false;
      if (!value || typeof value.available !== 'boolean'
        || (value.message !== undefined && typeof value.message !== 'string')
        || (value.voices !== undefined && (!Array.isArray(value.voices) || value.voices.some(voice =>
          !voice || typeof voice.id !== 'string' || !voice.id || typeof voice.name !== 'string' || !voice.name)))
        || (value.available && !value.voices?.length))
        throw new Error('Preview presets were invalid. Retry.');
      support = { ...value, voices: value.voices || [] };
      publish(value.available ? 'idle' : 'error', value.message || (value.available ? 'Choose a voice and text.' : 'Voice previews are unavailable.'));
      return value.available;
    } catch (error) { if (!disposed && mine === epoch) publish('error', friendlyPlaybackError(error)); return false; }
  }
  async function generate({ voiceId, text, language = 'en' }) {
    if (disposed) return false;
    if (!support?.available || !support.voices.some(voice => voice.id === voiceId)
      || typeof text !== 'string' || !text.trim() || text.length > 240 || language !== 'en') {
      publish('error', 'Choose an available voice and 1–240 characters of English text.'); return false;
    }
    cancelPending(); const mine = epoch;
    const submittedId = uuid(); requestId = submittedId;
    controller = new AbortController();
    const signal = AbortSignal.any([controller.signal, AbortSignal.timeout(120000)]);
    publish('generating', 'Preparing your private preview…');
    try {
      const response = await fetchImpl(endpoint, {
        method: 'POST', headers: { ...headers(), 'content-type': 'application/json' },
        body: JSON.stringify({ requestId: submittedId, voiceId, text, language }), signal,
        cache: 'no-store', credentials: 'omit', redirect: 'error',
      });
      if (!response.ok) { await json(response, signal); throw new Error('Preview failed. Retry.'); }
      if (response.headers.get('content-type')?.split(';')[0].trim().toLowerCase() !== 'audio/wav')
        throw new Error('Preview response was not WAV audio.');
      const bytes = await readBoundedArrayBuffer(response, 8 * 1024 * 1024, 'Preview audio is too large.', signal);
      inspectPcmWav(bytes);
      const value = JSON.parse(response.headers.get('x-qline-preview') || 'null');
      if (!value || value.provider !== 'Voicebox' || value.engine !== 'kokoro'
        || value.voiceId !== voiceId || value.bytes !== bytes.byteLength || typeof value.format !== 'string')
        throw new Error('Preview identity did not match the selected voice.');
      if (disposed || mine !== epoch) return false;
      requestId = null; metadata = value;
      objectUrl = urlApi.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
      bindMedia(objectUrl, mine);
      audio.src = objectUrl; audio.load();
      publish('ready', 'Preview ready. Choose Play preview.');
      return true;
    } catch (error) {
      if (!disposed && mine === epoch) { requestId = submittedId; cancelPending(); publish('error', friendlyPlaybackError(error)); }
      return false;
    }
  }
  async function play() {
    if (disposed || !objectUrl) return false;
    const mine = epoch;
    try { await audio.play(); if (disposed || mine !== epoch) return false; publish('playing', 'Playing your private preview.'); return true; }
    catch (error) { if (!disposed && mine === epoch) publish('error', friendlyPlaybackError(error)); return false; }
  }
  return { load, generate, play,
    stop() { if (!disposed) { cancelPending(); publish('idle', 'Preview stopped.'); } },
    dispose() { if (disposed) return; disposed = true; cancelPending(); },
  };
}
